import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Eye, EyeOff, HelpCircle, Clock, Mail, Loader2, X, ArrowRight } from 'lucide-react';

const LESSONSPACE_GUIDE = [
  'Log into your LessonSpace admin dashboard',
  'Go to Settings → API',
  'Click "Generate API Key"',
  'Copy the key and paste it below',
  'Optionally copy your Organisation ID from the URL bar',
];

const ZOOM_GUIDE = [
  'Go to marketplace.zoom.us and sign in',
  'Click Develop → Build App',
  'Create a Server-to-Server OAuth app',
  'Copy Account ID, Client ID, and Client Secret',
  'Add required scopes: meeting:read, recording:read',
  'Activate the app',
];

const TEAMS_GUIDE = [
  'In the Azure portal, open App registrations and click "New registration".',
  'Add API permissions (Application type): OnlineMeetingTranscript.Read.All and CallRecords.Read.All.',
  'Ask a tenant admin to click "Grant admin consent for <Your Tenant>" on the same view.',
  'Create a client secret and copy the VALUE (not the ID) — you will not see it again.',
  'Copy the Tenant ID and Application (client) ID from the Overview tab.',
  'Open Microsoft Teams PowerShell as a tenant admin and run:\nConnect-MicrosoftTeams\n$policy = New-CsApplicationAccessPolicy -Identity "TutorTracer" -AppIds "<your-client-id>" -Description "TutorTracer transcript sync"\nGrant-CsApplicationAccessPolicy -Identity "<admin-user>" -PolicyName "TutorTracer" -Global',
  'Wait up to 30 minutes for the policy to propagate across Microsoft 365.',
  'Paste the Tenant ID, Application (client) ID, and Client Secret below, then confirm both checkboxes.',
];

const MEET_GUIDE = [
  'Open Google Cloud Console, then APIs & Services → Library. Enable the Google Meet API and the Google Workspace Events API.',
  'Go to IAM & Admin → Service accounts. Create a new service account named "tutortracer-sync" and download a JSON key.',
  'Open the Google Workspace Admin console → Security → Access and data control → API controls → Domain-wide Delegation → Add new.',
  'Paste the service account\'s Client ID and authorize the scope: https://www.googleapis.com/auth/meetings.space.readonly',
  'Choose an admin email for the service account to impersonate (used to enumerate organisation-wide conferenceRecords).',
  'Paste the full JSON key below, enter the admin email, then confirm the delegation checkbox.',
];

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const IntegrationWizard = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [step, setStep] = useState(1);
  const [provider, setProvider] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showSecondary, setShowSecondary] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok: bool, message: string }
  const [connectedName, setConnectedName] = useState('');
  const [jsonError, setJsonError] = useState('');

  const [creds, setCreds] = useState({
    // Lessonspace
    apiKey: '', orgId: '',
    // Zoom
    accountId: '', clientId: '', clientSecret: '',
    // Teams
    teamsTenantId: '', teamsClientId: '', teamsClientSecret: '',
    teamsPolicyConfirmed: false, teamsConsentConfirmed: false,
    // Meet
    meetServiceAccountJson: '', meetImpersonateSubject: '',
    meetScopesConfirmed: false,
    // Other
    genericCreds: '',
  });

  useEffect(() => {
    if (!organizationId) return;
    // Load existing provider setting
    supabase.from('organizations').select('lessonspace_api_key, zoom_account_id').eq('id', organizationId).single()
      .then(({ data }) => {
        if (data?.lessonspace_api_key) setProvider('lessonspace');
        else if (data?.zoom_account_id) setProvider('zoom');
      });
  }, [organizationId]);

  const set = (k, v) => setCreds(c => ({ ...c, [k]: v }));

  const canSubmit = () => {
    if (provider === 'lessonspace') return creds.apiKey.trim().length > 10;
    if (provider === 'zoom') return creds.accountId && creds.clientId && creds.clientSecret;
    if (provider === 'msteams') {
      return (
        GUID_RE.test(creds.teamsTenantId.trim()) &&
        GUID_RE.test(creds.teamsClientId.trim()) &&
        creds.teamsClientSecret.trim().length > 0 &&
        creds.teamsPolicyConfirmed &&
        creds.teamsConsentConfirmed
      );
    }
    if (provider === 'googlemeet') {
      return (
        creds.meetServiceAccountJson.trim().length > 0 &&
        creds.meetImpersonateSubject.trim().length > 0 &&
        !jsonError &&
        creds.meetScopesConfirmed
      );
    }
    return creds.genericCreds.trim().length > 0;
  };

  const validateJsonOnBlur = () => {
    if (!creds.meetServiceAccountJson.trim()) {
      setJsonError('');
      return;
    }
    try {
      const parsed = JSON.parse(creds.meetServiceAccountJson);
      if (!parsed || typeof parsed !== 'object') throw new Error('Not an object');
      if (parsed.type !== 'service_account') {
        setJsonError('JSON is valid but does not look like a service account key (missing type: "service_account").');
        return;
      }
      setJsonError('');
    } catch (e) {
      setJsonError('Malformed JSON — check that you pasted the entire file including the outer { and }.');
    }
  };

  const resetTestResult = () => {
    setTestResult(null);
    setConnectedName('');
  };

  const runTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      let fnName = null;
      let body = null;

      if (provider === 'zoom') {
        fnName = 'save-zoom-credentials';
        body = {
          organizationId,
          accountId: creds.accountId.trim(),
          clientId: creds.clientId.trim(),
          clientSecret: creds.clientSecret.trim(),
          testConnection: true,
        };
      } else if (provider === 'msteams') {
        fnName = 'save-teams-credentials';
        body = {
          organizationId,
          tenantId: creds.teamsTenantId.trim(),
          clientId: creds.teamsClientId.trim(),
          clientSecret: creds.teamsClientSecret.trim(),
          teams_app_access_policy_confirmed: creds.teamsPolicyConfirmed,
          teams_admin_consent_confirmed: creds.teamsConsentConfirmed,
          testConnection: true,
        };
      } else if (provider === 'googlemeet') {
        fnName = 'save-googlemeet-credentials';
        body = {
          organizationId,
          serviceAccountJson: creds.meetServiceAccountJson,
          impersonateSubject: creds.meetImpersonateSubject.trim(),
          meet_workspace_scopes_confirmed: creds.meetScopesConfirmed,
          testConnection: true,
        };
      } else {
        setTesting(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke(fnName, { body });
      if (fnError) {
        setTestResult({ ok: false, message: fnError.message || 'Edge function invocation failed' });
      } else if (data?.testConnection?.ok) {
        const name = data.testConnection.displayName || data.testConnection.email || 'account';
        setConnectedName(name);
        setTestResult({ ok: true, message: `Connected as ${name}` });
      } else {
        const msg = data?.testConnection?.error || data?.error || 'Connection test failed';
        setTestResult({ ok: false, message: msg });
      }
    } catch (err) {
      setTestResult({ ok: false, message: err.message || 'Unexpected error during test' });
    }
    setTesting(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      if (provider === 'lessonspace') {
        await supabase.from('organizations').update({
          lessonspace_api_key: creds.apiKey.trim(),
          lessonspace_org_id: creds.orgId.trim() || null,
        }).eq('id', organizationId);
        setStep(3);
      } else if (provider === 'zoom') {
        const { data, error: fnError } = await supabase.functions.invoke('save-zoom-credentials', {
          body: {
            organizationId,
            accountId: creds.accountId.trim(),
            clientId: creds.clientId.trim(),
            clientSecret: creds.clientSecret.trim(),
          },
        });
        if (fnError) throw new Error(fnError.message);
        if (data?.testConnection) {
          setConnectedName(data.testConnection.displayName || data.testConnection.email || 'Zoom');
        }
        setStep(3);
      } else if (provider === 'msteams') {
        const { data, error: fnError } = await supabase.functions.invoke('save-teams-credentials', {
          body: {
            organizationId,
            tenantId: creds.teamsTenantId.trim(),
            clientId: creds.teamsClientId.trim(),
            clientSecret: creds.teamsClientSecret.trim(),
            teams_app_access_policy_confirmed: creds.teamsPolicyConfirmed,
            teams_admin_consent_confirmed: creds.teamsConsentConfirmed,
          },
        });
        if (fnError) throw new Error(fnError.message);
        if (data?.testConnection) {
          setConnectedName(data.testConnection.displayName || data.testConnection.email || 'Microsoft Teams');
        }
        setStep(3);
      } else if (provider === 'googlemeet') {
        const { data, error: fnError } = await supabase.functions.invoke('save-googlemeet-credentials', {
          body: {
            organizationId,
            serviceAccountJson: creds.meetServiceAccountJson,
            impersonateSubject: creds.meetImpersonateSubject.trim(),
            meet_workspace_scopes_confirmed: creds.meetScopesConfirmed,
          },
        });
        if (fnError) throw new Error(fnError.message);
        if (data?.testConnection) {
          setConnectedName(data.testConnection.displayName || data.testConnection.email || 'Google Meet');
        }
        setStep(3);
      } else {
        setStep(3);
      }
    } catch (err) {
      setError(err.message || 'Failed to save credentials');
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-4 py-2.5 text-sm rounded-lg border ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`;
  const labelClass = `text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`;
  const checkboxLabel = `flex items-start gap-2 text-xs cursor-pointer ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`;

  const providers = [
    { id: 'lessonspace', label: 'LessonSpace' },
    { id: 'zoom', label: 'Zoom' },
    { id: 'googlemeet', label: 'Google Meet' },
    { id: 'msteams', label: 'MS Teams' },
    { id: 'other', label: 'Other' },
  ];

  const providerLabel = () => {
    switch (provider) {
      case 'lessonspace': return 'LessonSpace';
      case 'zoom': return 'Zoom';
      case 'msteams': return 'Microsoft Teams';
      case 'googlemeet': return 'Google Meet';
      default: return 'Platform';
    }
  };

  const guideFor = () => {
    if (provider === 'lessonspace') return LESSONSPACE_GUIDE;
    if (provider === 'zoom') return ZOOM_GUIDE;
    if (provider === 'msteams') return TEAMS_GUIDE;
    if (provider === 'googlemeet') return MEET_GUIDE;
    return [];
  };

  const supportsTest = provider === 'zoom' || provider === 'msteams' || provider === 'googlemeet';
  const supportsGuide = provider === 'lessonspace' || provider === 'zoom' || provider === 'msteams' || provider === 'googlemeet';

  // GUID helper UI
  const guidInvalid = (v) => v && v.trim().length > 0 && !GUID_RE.test(v.trim());

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/settings')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Settings
      </button>

      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        Integration Setup
      </h1>

      {/* Step indicators */}
      <div className="flex items-center gap-3">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
              step > s ? 'bg-emerald-400/10 text-emerald-400'
              : step === s ? isDark ? 'accent-gradient text-white' : 'accent-gradient text-white'
              : isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
            }`}>
              {step > s ? <Check className="h-3.5 w-3.5" /> : s}
            </div>
            {s < 3 && <div className={`w-12 h-px ${step > s ? 'bg-emerald-400' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />}
          </div>
        ))}
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg text-xs bg-red-500/10 text-red-400 border border-red-500/20">{error}</div>
      )}

      {/* Step 1: Select Provider */}
      {step === 1 && (
        <div className={`${cardClass} p-6`}>
          <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Select your platform</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {providers.map(p => (
              <button
                key={p.id}
                onClick={() => { setProvider(p.id); setStep(2); resetTestResult(); }}
                className={`px-4 py-3 text-sm rounded-lg border text-center transition-colors ${
                  provider === p.id
                    ? isDark ? 'border-[#7662ac] bg-[#7662ac]/10 text-white' : 'border-[#586f8d] bg-[#586f8d]/10 text-[#1a1a1a]'
                    : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:border-[#d0d0d0]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Credentials */}
      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className={`${cardClass} p-6 ${showGuide ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                {providerLabel()} Credentials
              </h3>
              {supportsGuide && (
                <button onClick={() => setShowGuide(!showGuide)} className={`flex items-center gap-1 text-xs ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>
                  <HelpCircle className="h-3.5 w-3.5" /> {showGuide ? 'Hide' : 'Setup'} Guide
                </button>
              )}
            </div>

            {provider === 'lessonspace' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="ls-api-key" className={labelClass}>API Key</label>
                  <div className="relative">
                    <input
                      id="ls-api-key"
                      type={showKey ? 'text' : 'password'}
                      value={creds.apiKey}
                      onChange={e => set('apiKey', e.target.value)}
                      placeholder="ls_..."
                      className={inputClass}
                    />
                    <button type="button" aria-label={showKey ? 'Hide API key' : 'Show API key'} onClick={() => setShowKey(!showKey)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="ls-org-id" className={labelClass}>Organisation ID <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>(optional)</span></label>
                  <input id="ls-org-id" value={creds.orgId} onChange={e => set('orgId', e.target.value)} placeholder="org_..." className={inputClass} />
                </div>
              </div>
            )}

            {provider === 'zoom' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="zoom-account-id" className={labelClass}>Account ID</label>
                  <input id="zoom-account-id" value={creds.accountId} onChange={e => { set('accountId', e.target.value); resetTestResult(); }} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="zoom-client-id" className={labelClass}>Client ID</label>
                  <input id="zoom-client-id" value={creds.clientId} onChange={e => { set('clientId', e.target.value); resetTestResult(); }} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="zoom-client-secret" className={labelClass}>Client Secret</label>
                  <div className="relative">
                    <input
                      id="zoom-client-secret"
                      type={showKey ? 'text' : 'password'}
                      value={creds.clientSecret}
                      onChange={e => { set('clientSecret', e.target.value); resetTestResult(); }}
                      className={inputClass}
                    />
                    <button type="button" aria-label={showKey ? 'Hide client secret' : 'Show client secret'} onClick={() => setShowKey(!showKey)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {provider === 'msteams' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="teams-tenant-id" className={labelClass}>Azure AD Tenant ID</label>
                  <input
                    id="teams-tenant-id"
                    value={creds.teamsTenantId}
                    onChange={e => { set('teamsTenantId', e.target.value); resetTestResult(); }}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className={inputClass}
                  />
                  {guidInvalid(creds.teamsTenantId) && (
                    <p className="mt-1 text-xs text-red-400">Must be a valid GUID (8-4-4-4-12 hex format).</p>
                  )}
                </div>
                <div>
                  <label htmlFor="teams-client-id" className={labelClass}>Application (client) ID</label>
                  <input
                    id="teams-client-id"
                    value={creds.teamsClientId}
                    onChange={e => { set('teamsClientId', e.target.value); resetTestResult(); }}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className={inputClass}
                  />
                  {guidInvalid(creds.teamsClientId) && (
                    <p className="mt-1 text-xs text-red-400">Must be a valid GUID (8-4-4-4-12 hex format).</p>
                  )}
                </div>
                <div>
                  <label htmlFor="teams-client-secret" className={labelClass}>Client Secret</label>
                  <div className="relative">
                    <input
                      id="teams-client-secret"
                      type={showKey ? 'text' : 'password'}
                      value={creds.teamsClientSecret}
                      onChange={e => { set('teamsClientSecret', e.target.value); resetTestResult(); }}
                      placeholder="Paste the VALUE of the secret (not the ID)"
                      className={inputClass}
                    />
                    <button type="button" aria-label={showKey ? 'Hide client secret' : 'Show client secret'} onClick={() => setShowKey(!showKey)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="pt-2 space-y-2">
                  <label htmlFor="teams-policy-confirm" className={checkboxLabel}>
                    <input
                      id="teams-policy-confirm"
                      type="checkbox"
                      checked={creds.teamsPolicyConfirmed}
                      onChange={e => { set('teamsPolicyConfirmed', e.target.checked); resetTestResult(); }}
                      className="mt-0.5"
                    />
                    <span>
                      I have granted the app access policy in Teams PowerShell (
                      <code className={isDark ? 'text-[#c4b5fd]' : 'text-[#586f8d]'}>Grant-CsApplicationAccessPolicy</code>
                      ).
                    </span>
                  </label>
                  <label htmlFor="teams-consent-confirm" className={checkboxLabel}>
                    <input
                      id="teams-consent-confirm"
                      type="checkbox"
                      checked={creds.teamsConsentConfirmed}
                      onChange={e => { set('teamsConsentConfirmed', e.target.checked); resetTestResult(); }}
                      className="mt-0.5"
                    />
                    <span>
                      Admin consent granted for <code className={isDark ? 'text-[#c4b5fd]' : 'text-[#586f8d]'}>OnlineMeetingTranscript.Read.All</code>.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {provider === 'googlemeet' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="meet-json" className={labelClass}>Service account JSON</label>
                  <div className="relative">
                    <textarea
                      id="meet-json"
                      value={showSecondary ? creds.meetServiceAccountJson : (creds.meetServiceAccountJson ? '•'.repeat(Math.min(creds.meetServiceAccountJson.length, 200)) : '')}
                      onChange={e => { set('meetServiceAccountJson', e.target.value); resetTestResult(); }}
                      onBlur={validateJsonOnBlur}
                      rows={8}
                      placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key_id": "...",\n  "private_key": "-----BEGIN PRIVATE KEY-----\\n...",\n  "client_email": "...@...iam.gserviceaccount.com",\n  ...\n}'}
                      className={`${inputClass} resize-y font-mono text-xs ${showSecondary ? '' : 'tracking-tight'}`}
                      readOnly={!showSecondary && !!creds.meetServiceAccountJson}
                      onFocus={() => { if (!showSecondary) setShowSecondary(true); }}
                    />
                    <button type="button" aria-label={showSecondary ? 'Hide JSON' : 'Show JSON'} onClick={() => setShowSecondary(!showSecondary)} className={`absolute right-3 top-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      {showSecondary ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {jsonError && (
                    <p className="mt-1 text-xs text-red-400">{jsonError}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="meet-impersonate" className={labelClass}>Admin email to impersonate</label>
                  <input
                    id="meet-impersonate"
                    type="email"
                    value={creds.meetImpersonateSubject}
                    onChange={e => { set('meetImpersonateSubject', e.target.value); resetTestResult(); }}
                    placeholder="admin@your-workspace.com"
                    className={inputClass}
                  />
                </div>

                <div className="pt-2">
                  <label htmlFor="meet-scope-confirm" className={checkboxLabel}>
                    <input
                      id="meet-scope-confirm"
                      type="checkbox"
                      checked={creds.meetScopesConfirmed}
                      onChange={e => { set('meetScopesConfirmed', e.target.checked); resetTestResult(); }}
                      className="mt-0.5"
                    />
                    <span>
                      DWD scope <code className={isDark ? 'text-[#c4b5fd]' : 'text-[#586f8d]'}>https://www.googleapis.com/auth/meetings.space.readonly</code> delegated to this service account in Workspace Admin console.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {provider === 'other' && (
              <div>
                <label htmlFor="generic-creds" className={labelClass}>Credentials / Notes</label>
                <textarea id="generic-creds" value={creds.genericCreds} onChange={e => set('genericCreds', e.target.value)} rows={4} placeholder="Paste credentials or describe your setup..." className={`${inputClass} resize-none`} />
              </div>
            )}

            {/* Test connection result */}
            {supportsTest && testResult && (
              <div className={`mt-4 px-3 py-2 rounded-lg text-xs border flex items-start gap-2 ${
                testResult.ok
                  ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                  : 'bg-red-500/10 border-red-500/20 text-red-400'
              }`}>
                {testResult.ok ? <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> : <X className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                <span className="break-words whitespace-pre-wrap">{testResult.message}</span>
              </div>
            )}

            <div className="flex items-center gap-3 mt-6 flex-wrap">
              <button onClick={() => { setStep(1); resetTestResult(); }} className={`text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>Back</button>

              {supportsTest && (
                <button
                  type="button"
                  onClick={runTestConnection}
                  disabled={!canSubmit() || testing || loading}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 border ${isDark ? 'border-[#27272a] text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {testing ? 'Testing connection…' : 'Test connection'}
                </button>
              )}

              <button
                onClick={handleSubmit}
                disabled={!canSubmit() || loading || testing}
                className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Connect
              </button>
            </div>

            {supportsTest && testResult && !testResult.ok && (
              <p className={`mt-3 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                Credentials saved. Connection test failed: {testResult.message}. You can fix the issue and re-test without re-entering.
              </p>
            )}
          </div>

          {/* Guide panel */}
          {showGuide && (
            <div className={`${cardClass} p-5 lg:col-span-2`}>
              <h4 className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Setup Guide</h4>
              <ol className={`space-y-3 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                {guideFor().map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium ${isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'}`}>{i + 1}</span>
                    <span className="whitespace-pre-wrap leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Done */}
      {step === 3 && (
        <div className={`${cardClass} p-8 text-center`}>
          <div className="w-16 h-16 rounded-full accent-gradient mx-auto mb-4 flex items-center justify-center">
            {connectedName ? <Check className="h-8 w-8 text-white" /> : <Loader2 className="h-8 w-8 text-white animate-spin" />}
          </div>
          <h3 className={`text-lg font-semibold font-[Manrope] mb-2 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
            {connectedName ? `Connected as ${connectedName}` : 'Connection Underway'}
          </h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {connectedName
              ? 'Credentials saved. Turn on continuous sync in Settings → Integrations whenever you are ready.'
              : "We're syncing your lessons. This may take a few minutes to a few hours depending on volume."}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-lg mx-auto">
            {[
              { icon: Clock, text: 'Initial sync takes minutes to hours' },
              { icon: Mail, text: "You'll get an email when ready" },
              { icon: HelpCircle, text: 'Contact support if issues arise' },
            ].map((item, i) => (
              <div key={i} className={`p-3 rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
                <item.icon className={`h-4 w-4 mx-auto mb-1.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} strokeWidth={1.5} />
                <p className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{item.text}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
            <button
              onClick={() => navigate('/settings?tab=integrations')}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
            >
              Next: configure sync <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate('/')}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors border ${isDark ? 'border-[#27272a] text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationWizard;
